import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import { Button, Form } from 'react-bootstrap';
import { getChecklistTemplatesByTeam, createChecklistFromTemplates, addItemsFromTemplate } from '../../api/checklist-queries';
import { useNotifications } from '../../notifications';
import { ReactComponent as ChecklistIcon } from '../../shared/icons/checklist-icon.svg';
import styles from './AddTemplateItemsForm.module.scss';

interface AddTemplateItemsFormProps {
  dealId: string;
  teamId: string;
  checklists: Array<{ id: string }>;
  onSuccess: () => void;
  onCancel: () => void;
  refetch: () => Promise<any>;
}

interface ChecklistTemplate {
  id: string;
  name: string;
  description?: string | null;
  sharedToAffiliatedWorkspaces: boolean;
  items?: {
    id: string;
    content: string;
    displayOrder: number;
    defaultDueOffsetDays?: number | null;
    children?: Array<{
      id: string;
      content: string;
      displayOrder: number;
      defaultDueOffsetDays?: number | null;
    }>;
  }[];
}

export const AddTemplateItemsForm: React.FC<AddTemplateItemsFormProps> = ({
  dealId,
  teamId,
  checklists,
  onSuccess,
  onCancel,
  refetch,
}) => {
  const [, { success: notifySuccess, error: notifyError }] = useNotifications();
  const [selectedTemplates, setSelectedTemplates] = useState<Set<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch templates
  const { data, loading } = useQuery(getChecklistTemplatesByTeam, {
    variables: { teamId },
  });

  // Mutations
  const [createChecklist] = useMutation(createChecklistFromTemplates);
  const [addItems] = useMutation(addItemsFromTemplate);

  const templates: ChecklistTemplate[] = data?.checklistTemplatesByTeam || [];

  const toggleTemplate = (templateId: string) => {
    setSelectedTemplates((prev) => {
      const next = new Set(prev);
      if (next.has(templateId)) {
        next.delete(templateId);
      } else {
        next.add(templateId);
      }
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedTemplates.size === 0) {
      notifyError({
        message: {
          id: 'checklist.select.template',
          defaultMessage: 'Please select at least one template',
        },
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const selectedIds = Array.from(selectedTemplates);

      if (checklists.length === 0) {
        // No checklist exists - create with templates
        const result = await createChecklist({
          variables: {
            dealId,
            name: 'Checklist',
            templateIds: selectedIds,
          },
        });

        if (result.data?.createChecklistFromTemplates?.errors?.length) {
          notifyError({
            message: {
              id: 'checklist.create.error',
              defaultMessage: result.data.createChecklistFromTemplates.errors[0] || 'Failed to add items',
            },
          });
          setIsSubmitting(false);
          return;
        }
      } else {
        // Checklist exists - add items from each template
        const checklistId = checklists[0].id;

        for (const templateId of selectedIds) {
          const result = await addItems({
            variables: { checklistId, templateId },
          });

          if (result.data?.addItemsFromTemplate?.errors?.length) {
            notifyError({
              message: {
                id: 'checklist.add.items.error',
                defaultMessage: result.data.addItemsFromTemplate.errors[0] || 'Failed to add items from template',
              },
            });
            setIsSubmitting(false);
            return;
          }
        }
      }

      notifySuccess({
        message: {
          id: 'checklist.items.added.success',
          defaultMessage: 'Items added successfully',
        },
      });

      await refetch();
      onSuccess();
    } catch (error) {
      notifyError({
        message: {
          id: 'checklist.add.items.error',
          defaultMessage: 'Failed to add items',
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTotalItemCount = (template: ChecklistTemplate): number => {
    let count = template.items.length;
    template.items.forEach((item) => {
      if (item.children) {
        count += item.children.length;
      }
    });
    return count;
  };

  return (
    <div className={styles.addTemplateItemsForm}>
      <Form onSubmit={handleSubmit} className={styles.addTemplateItemsForm__form}>
        {/* Template Selection */}
        <div className={styles.addTemplateItemsForm__templates}>
          <h4 className={styles.addTemplateItemsForm__templatesTitle}>
            <FormattedMessage id="checklist.add.select.templates" defaultMessage="Select Templates" />
            <span className={styles.addTemplateItemsForm__required}>*</span>
          </h4>

          {loading ? (
            <div className={styles.addTemplateItemsForm__loading}>
              <FormattedMessage id="checklist.loading.templates" defaultMessage="Loading templates..." />
            </div>
          ) : templates.length === 0 ? (
            <div className={styles.addTemplateItemsForm__empty}>
              <ChecklistIcon className={styles.addTemplateItemsForm__emptyIcon} />
              <p className={styles.addTemplateItemsForm__emptyText}>
                <FormattedMessage
                  id="checklist.no.templates"
                  defaultMessage="No templates available. Contact your team administrator to create checklist templates."
                />
              </p>
            </div>
          ) : (
            <div className={styles.addTemplateItemsForm__templatesList}>
              {templates.map((template) => {
                const isSelected = selectedTemplates.has(template.id);
                const itemCount = getTotalItemCount(template);

                return (
                  <div
                    key={template.id}
                    className={`${styles.templateItem} ${isSelected ? styles.templateItem__selected : ''}`}
                    onClick={() => toggleTemplate(template.id)}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleTemplate(template.id)}
                      className={styles.templateItem__checkbox}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className={styles.templateItem__content}>
                      <span className={styles.templateItem__name}>{template.name}</span>
                      <span className={styles.templateItem__count}>
                        {itemCount} {itemCount === 1 ? 'item' : 'items'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className={styles.addTemplateItemsForm__actions}>
          <Button variant="secondary" onClick={onCancel} disabled={isSubmitting}>
            <FormattedMessage id="checklist.add.cancel" defaultMessage="Cancel" />
          </Button>
          <Button variant="primary" type="submit" disabled={isSubmitting || loading || selectedTemplates.size === 0}>
            {isSubmitting ? (
              <FormattedMessage id="checklist.add.adding" defaultMessage="Adding..." />
            ) : (
              <FormattedMessage id="checklist.add.items" defaultMessage="Add Items" />
            )}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default AddTemplateItemsForm;
